import {
  LAYOUT_COMPONENT_TYPE,
  REGISTRY_BLOCK,
  type LayoutBlock,
} from '@web-studio/shared-schemas';
import { loc3 } from '../locales';
import { FIXTURE_BLOCK_IDS } from '../uuids';

const CATEGORY_IMAGES = [
  'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=900&h=720&fit=crop',
  'https://images.unsplash.com/photo-1473093295043-cdd812d0e601?w=900&h=720&fit=crop',
  'https://images.unsplash.com/photo-1540189549336-e6e99c3679fe?w=900&h=720&fit=crop',
] as const;

const DISH_IMAGES = [
  'https://images.unsplash.com/photo-1608897013039-887f21d8c804?w=720&h=720&fit=crop',
  'https://images.unsplash.com/photo-1626645738196-afdf8ea25b14?w=720&h=720&fit=crop',
  'https://images.unsplash.com/photo-1551183053-bf91a1d81141?w=720&h=720&fit=crop',
  'https://images.unsplash.com/photo-1476124369491-e7addf5db371?w=720&h=720&fit=crop',
  'https://images.unsplash.com/photo-1467003909585-2f8a72700288?w=720&h=720&fit=crop',
  'https://images.unsplash.com/photo-1432139555190-58524dae6a55?w=720&h=720&fit=crop',
] as const;

function menuColumnsWithCategoryImages(locale: 'de' | 'en' | 'vi') {
  const titles =
    locale === 'de'
      ? (['Vorspeisen', 'Pasta', 'Hauptgerichte'] as const)
      : locale === 'en'
        ? (['Starters', 'Pasta', 'Mains'] as const)
        : (['Khai vị', 'Pasta', 'Món chính'] as const);
  const items =
    locale === 'de'
      ? ([
          [
            { name: 'Burrata', price: '14', description: 'Tomaten, Basilikumöl' },
            { name: 'Carpaccio', price: '16', description: 'Rucola, Parmesan' },
          ],
          [
            { name: 'Tagliatelle al ragù', price: '24', description: 'Geschmortes Rind' },
            { name: 'Risotto ai funghi', price: '22', description: 'Waldpilze' },
          ],
          [
            { name: 'Branzino', price: '32', description: 'Zitrone, Kapern' },
            { name: 'Entenbrust', price: '34', description: 'Kirschen' },
          ],
        ] as const)
      : locale === 'en'
        ? ([
            [
              { name: 'Burrata', price: '14', description: 'Heirloom tomato, basil oil' },
              { name: 'Carpaccio', price: '16', description: 'Arugula, aged parmesan' },
            ],
            [
              { name: 'Tagliatelle al ragù', price: '24', description: 'Slow-braised beef' },
              { name: 'Risotto ai funghi', price: '22', description: 'Wild mushrooms' },
            ],
            [
              { name: 'Branzino', price: '32', description: 'Lemon, capers, herbs' },
              { name: 'Duck breast', price: '34', description: 'Cherry gastrique' },
            ],
          ] as const)
        : ([
            [
              { name: 'Burrata', price: '14', description: 'Cà chua, dầu húng quế' },
              { name: 'Carpaccio', price: '16', description: 'Rucola, parmesan' },
            ],
            [
              { name: 'Tagliatelle al ragù', price: '24', description: 'Bò hầm chậm' },
              { name: 'Risotto ai funghi', price: '22', description: 'Nấm rừng' },
            ],
            [
              { name: 'Branzino', price: '32', description: 'Chanh, caper, thảo mộc' },
              { name: 'Ức vịt', price: '34', description: 'Sốt cherry' },
            ],
          ] as const);

  return titles.map((title, i) => {
    const columnItems = items[i] ?? [];
    return {
      title,
      imageUrl: CATEGORY_IMAGES[i],
      items: columnItems.map((item, itemIndex) => {
        const tagged = {
          ...item,
          imageUrl: DISH_IMAGES[i * 2 + itemIndex],
        };
        // Demo legal + dietary marks for Design Lab (shared across locales).
        if (i === 0 && itemIndex === 0) {
          return {
            ...tagged,
            allergenIds: ['milk'],
            dietaryTagIds: ['vegetarian', 'organic'],
          };
        }
        if (i === 0 && itemIndex === 1) {
          return {
            ...tagged,
            allergenIds: ['cereals_containing_gluten', 'milk'],
            additiveIds: ['preservative'],
          };
        }
        if (i === 1 && itemIndex === 1) {
          return {
            ...tagged,
            allergenIds: ['cereals_containing_gluten', 'milk'],
            dietaryTagIds: ['vegetarian'],
          };
        }
        if (i === 2 && itemIndex === 0) {
          return {
            ...tagged,
            allergenIds: ['fish'],
            additiveIds: ['colorant'],
          };
        }
        return tagged;
      }),
    };
  });
}

const localizedMenuData = {
  de: JSON.stringify(menuColumnsWithCategoryImages('de')),
  en: JSON.stringify(menuColumnsWithCategoryImages('en')),
  vi: JSON.stringify(menuColumnsWithCategoryImages('vi')),
};

const baseMenuContent = {
  sectionTitle: loc3('Aus unserer Küche', 'From our kitchen', 'Từ bếp của chúng tôi'),
  sectionSubtitle: loc3(
    'Saisonal, aus der Region',
    'Seasonal, from the region',
    'Theo mùa, từ vùng địa phương',
  ),
  menuData: localizedMenuData,
};

export const menuPdfCardDefault: LayoutBlock = {
  blockId: FIXTURE_BLOCK_IDS.menuPdfCard,
  componentType: LAYOUT_COMPONENT_TYPE.menuGrid3Column,
  registryBlockKey: REGISTRY_BLOCK.presentation.staticMenuPdfCard,
  displayOrder: 2,
  content: {
    sectionTitle: loc3('Speisekarte', 'Menu', 'Thực đơn'),
    sectionSubtitle: loc3(
      'Als PDF — blättern oder herunterladen',
      'As PDF — browse or download',
      'Dạng PDF — xem hoặc tải xuống',
    ),
    pdfCta: loc3(
      'Speisekarte öffnen',
      'Open menu PDF',
      'Mở thực đơn PDF',
    ),
  },
  media: {
    menuPdf: '/platform/defaults/menu-sample.pdf',
  },
};

export const menuCategoryGalleryDefault: LayoutBlock = {
  blockId: FIXTURE_BLOCK_IDS.menuCategoryGallery,
  componentType: LAYOUT_COMPONENT_TYPE.menuGrid3Column,
  registryBlockKey: REGISTRY_BLOCK.presentation.staticMenuCategoryGallery,
  displayOrder: 2,
  content: baseMenuContent,
  media: {
    categoryImage0: CATEGORY_IMAGES[0],
    categoryImage1: CATEGORY_IMAGES[1],
    categoryImage2: CATEGORY_IMAGES[2],
  },
};

export const menuDishCardsDefault: LayoutBlock = {
  blockId: FIXTURE_BLOCK_IDS.menuDishCards,
  componentType: LAYOUT_COMPONENT_TYPE.menuGrid3Column,
  registryBlockKey: REGISTRY_BLOCK.presentation.staticMenuDishCards,
  displayOrder: 2,
  content: baseMenuContent,
  media: {
    itemImage_0_0: DISH_IMAGES[0],
    itemImage_0_1: DISH_IMAGES[1],
    itemImage_1_0: DISH_IMAGES[2],
    itemImage_1_1: DISH_IMAGES[3],
    itemImage_2_0: DISH_IMAGES[4],
    itemImage_2_1: DISH_IMAGES[5],
  },
};

export const menuEditorialBoardDefault: LayoutBlock = {
  blockId: FIXTURE_BLOCK_IDS.menuEditorialBoard,
  componentType: LAYOUT_COMPONENT_TYPE.menuGrid3Column,
  registryBlockKey: REGISTRY_BLOCK.presentation.staticMenuEditorialBoard,
  displayOrder: 2,
  content: {
    sectionTitle: loc3('Speisekarte', 'Menu', 'Thực đơn'),
    sectionSubtitle: loc3(
      'Abendkarte · ab 18 Uhr',
      'Evening menu · from 6 pm',
      'Thực đơn tối · từ 18 giờ',
    ),
    menuData: localizedMenuData,
  },
  media: {},
};

function streetBoardColumns(locale: 'de' | 'en' | 'vi') {
  const titles =
    locale === 'de'
      ? (['Im Brot', 'Dürüm & Box', 'Teller', 'Menüs', 'Getränke'] as const)
      : locale === 'en'
        ? (['In bread', 'Wrap & box', 'Plates', 'Combos', 'Drinks'] as const)
        : (['Trong bánh', 'Cuốn & hộp', 'Đĩa', 'Combo', 'Đồ uống'] as const);
  return titles.map((title, i) => ({
    title,
    imageUrl: CATEGORY_IMAGES[i % CATEGORY_IMAGES.length],
    items: [
      {
        name:
          locale === 'de'
            ? ['Döner im Brot', 'Dürüm', 'Döner Teller', 'Döner-Menü', 'Ayran'][i]!
            : locale === 'en'
              ? ['Doner in bread', 'Durum', 'Doner plate', 'Doner combo', 'Ayran'][i]!
              : ['Döner bánh', 'Dürüm', 'Đĩa döner', 'Combo döner', 'Ayran'][i]!,
        price: ['7,50 €', '9,00 €', '12,50 €', '11,90 €', '2,50 €'][i]!,
        description:
          locale === 'de'
            ? 'Frisch zubereitet'
            : locale === 'en'
              ? 'Made to order'
              : 'Làm theo yêu cầu',
      },
      {
        name:
          locale === 'de'
            ? ['Käse-Döner', 'Döner Box', 'Falafel Teller', 'Currywurst-Menü', 'Wasser'][i]!
            : locale === 'en'
              ? ['Cheese doner', 'Doner box', 'Falafel plate', 'Currywurst combo', 'Water'][i]!
              : ['Döner phô mai', 'Hộp döner', 'Đĩa falafel', 'Combo currywurst', 'Nước'][i]!,
        price: ['8,50 €', '8,50 €', '11,00 €', '9,90 €', '1,50 €'][i]!,
        description: '',
      },
    ],
  }));
}

const streetBoardMenuData = {
  de: JSON.stringify(streetBoardColumns('de')),
  en: JSON.stringify(streetBoardColumns('en')),
  vi: JSON.stringify(streetBoardColumns('vi')),
};

export const menuStreetBoardDefault: LayoutBlock = {
  blockId: FIXTURE_BLOCK_IDS.menuStreetBoard,
  componentType: LAYOUT_COMPONENT_TYPE.menuGrid3Column,
  registryBlockKey: REGISTRY_BLOCK.presentation.staticMenuStreetBoard,
  displayOrder: 2,
  content: {
    sectionTitle: loc3('Karte', 'Menu', 'Thực đơn'),
    sectionSubtitle: loc3(
      'Im Brot, Teller und Menüs — tippen Sie eine Kategorie.',
      'In bread, plates, and combos — tap a category.',
      'Bánh, đĩa và combo — chạm một danh mục.',
    ),
    menuData: streetBoardMenuData,
  },
  media: {
    categoryImage0: CATEGORY_IMAGES[0],
    categoryImage1: CATEGORY_IMAGES[1],
    categoryImage2: CATEGORY_IMAGES[2],
    categoryImage3: CATEGORY_IMAGES[0],
    categoryImage4: CATEGORY_IMAGES[1],
  },
};
